import type { CityLight, PlanAssetData, PlanDesignComponent } from "../api/types";

export type PlanAssetRuntimeData = PlanAssetData & {
  defaultScale?: number;
  defaultRotation?: number;
  anchorX?: number;
  anchorY?: number;
  renderOrder?: number;
  visualOffsetX?: number;
  visualOffsetY?: number;
  frames?: string[];
  frameDelayMs?: number;
  lights?: CityLight[];
};

export function runtimePlanAsset(asset: PlanAssetData): PlanAssetRuntimeData {
  return asset as PlanAssetRuntimeData;
}

function rgba(hex: string, alpha: number): string {
  const normalized = /^#[0-9a-f]{6}$/i.test(hex) ? hex.slice(1) : "ffd76f";
  const value = Number.parseInt(normalized, 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `rgba(${r},${g},${b},${Math.max(0, Math.min(1, alpha))})`;
}

/**
 * DOM counterpart of the existing Pixi AssetSprite light renderer.
 * It consumes the exact same CityLight schema and preserves the same
 * intensity/radius/source semantics for the 2D PlanDesigner/StorefrontTour.
 */
export function appendPlanAssetLights(
  frame: HTMLElement,
  assetValue: PlanAssetData,
  component: PlanDesignComponent,
  baseZ: number,
): void {
  const asset = runtimePlanAsset(assetValue);
  const lights = asset.lights ?? [];
  if (!lights.length) return;

  const scale = Math.max(0.001, Number(component.scale) || 1);
  const rotation = Number(component.rotation) || 0;
  const flip = component.flipX ? -1 : 1;
  const renderOrder = Number(asset.renderOrder) || 0;

  for (const light of lights) {
    const intensity = Math.max(0, Math.min(2, Number(light.intensity ?? 0.9)));
    if (intensity <= 0) continue;

    const root = document.createElement("span");
    root.dataset.planAssetLight = String(asset.id);
    root.setAttribute("aria-hidden", "true");
    root.style.position = "absolute";
    root.style.left = `${component.x}%`;
    root.style.top = `${component.y}%`;
    root.style.width = "0";
    root.style.height = "0";
    root.style.pointerEvents = "none";
    root.style.zIndex = String(baseZ + component.layer + renderOrder);
    root.style.transform =
      `translate(${asset.visualOffsetX ?? 0}px,${asset.visualOffsetY ?? 0}px) rotate(${rotation}deg) scale(${scale}) scaleX(${flip})`;
    root.style.transformOrigin = "0 0";

    const radius = Math.max(4, Math.min(2000, Number(light.radius ?? 72)));
    const glow = document.createElement("span");
    glow.style.position = "absolute";
    glow.style.left = `${Number(light.offsetX ?? 0)}px`;
    glow.style.top = `${Number(light.offsetY ?? 0)}px`;
    glow.style.width = `${radius * 2}px`;
    glow.style.height = `${radius * 2}px`;
    glow.style.borderRadius = "50%";
    glow.style.transform = "translate(-50%,-50%)";
    glow.style.pointerEvents = "none";
    glow.style.mixBlendMode = "screen";
    glow.style.background = `radial-gradient(circle,
      ${rgba(light.color, Math.min(0.36, 0.16 * intensity))} 0%,
      ${rgba(light.color, Math.min(0.22, 0.09 * intensity))} 34%,
      ${rgba(light.color, Math.min(0.14, 0.05 * intensity))} 62%,
      ${rgba(light.color, 0)} 100%)`;

    if (light.showSource !== false) {
      const source = document.createElement("span");
      const sourceSize = Math.max(1, Number(light.sourceSize ?? 3));
      source.style.position = "absolute";
      source.style.left = `${Number(light.offsetX ?? 0)}px`;
      source.style.top = `${Number(light.offsetY ?? 0)}px`;
      source.style.width = `${sourceSize * 2}px`;
      source.style.height = `${sourceSize * 2}px`;
      source.style.borderRadius = "50%";
      source.style.transform = "translate(-50%,-50%)";
      source.style.background = rgba(
        light.color,
        Math.min(0.95, 0.55 + intensity * 0.18),
      );
      source.style.boxShadow = `0 0 ${Math.max(4, sourceSize * 2)}px ${rgba(light.color, 0.65)}`;
      source.style.pointerEvents = "none";
      root.appendChild(source);
    }

    root.appendChild(glow);
    frame.appendChild(root);
  }
}

export function planAssetImageTransform(
  assetValue: PlanAssetData,
  component: PlanDesignComponent,
): string {
  const asset = runtimePlanAsset(assetValue);
  const offsetX = Number(asset.visualOffsetX ?? 0);
  const offsetY = Number(asset.visualOffsetY ?? 0);
  return `translate(calc(-${(asset.anchorX ?? 0.5) * 100}% + ${offsetX}px),calc(-${(asset.anchorY ?? 0.5) * 100}% + ${offsetY}px)) rotate(${component.rotation}deg) scale(${component.scale}) scaleX(${component.flipX ? -1 : 1})`;
}

export function planAssetZIndex(
  assetValue: PlanAssetData,
  component: PlanDesignComponent,
  baseZ: number,
): number {
  const asset = runtimePlanAsset(assetValue);
  return baseZ + component.layer + (Number(asset.renderOrder) || 0);
}
